import React, { useCallback, useId, useState } from "react";
import "./App.css";
import CaseIntake from "./CaseIntake";
import ProductEvidenceLibrary from "./ProductEvidenceLibrary";
import ManufacturerProductDirectory from "./ManufacturerProductDirectory";
import ReviewedConsultation from "./ReviewedConsultation";
import { normalizeReviewedConsultation } from "./reviewedConsultation";
import { isEmergencyDeliveryFallback, isEmergencyFailure } from "./emergencyFailure";
import {
  buildGovernedRequest,
  interactionIsBlocked,
  normalizeClinicalInteraction,
} from "./clinicalInteraction";
import {
  EVIDENCE_MODE,
  normalizeEvidenceResponse,
  normalizePromptLibrary,
  normalizeRequestDisposition,
} from "./evidenceInteraction";

const EMPTY_RESPONSE = Object.freeze({
  response_type: "PATIENT",
  diagnosis: null,
  treatment_plan: [],
  plan_steps: [],
  diagnostics: [],
  product_recommendations: [],
  product_option_contract_version: null,
  product_selection_summary: null,
  product_selection_disclaimer: null,
  billing_codes: [],
  lcd_ncd_summary: null,
  answer: null,
  answer_markdown: null,
  clinical_rationale: null,
  explanation_for_clinician: null,
  referrals: [],
  citations: [],
  reasoning: null,
  clinical_interaction: null,
  conversation_state: null,
  request_disposition: null,
});

const DIRECT_CLINICAL_PAYLOAD_KEYS = Object.freeze([
  "diagnosis",
  "treatment_plan",
  "plan_steps",
  "diagnostics",
  "product_recommendations",
  "product_option_contract_version",
  "product_selection_summary",
  "product_selection_disclaimer",
  "billing_codes",
  "lcd_ncd_summary",
  "answer",
  "answer_markdown",
  "clinical_rationale",
  "explanation_for_clinician",
  "referrals",
  "citations",
  "clinical_interaction",
  "conversation_state",
  "request_disposition",
  "evidence_contract_version",
  "evidence_key_points",
  "limitations",
  "related_prompts",
]);

const DIAG_CATEGORY_TITLES = {
  baseline_labs: "Baseline and chronic wound labs",
  infection_labs: "Infection workup",
  nutrition_labs: "Nutrition and protein status",
  glycemic_control: "Glycemic control",
  coagulation: "Coagulation status",
  vascular_noninvasive: "Vascular assessment",
  vascular_invasive: "Advanced vascular imaging",
  imaging_plain_film: "Plain film imaging",
  imaging_advanced: "Advanced imaging",
  perfusion: "Perfusion testing",
  biopsy: "Biopsy and pathology",
  other: "Other diagnostics",
};

const DIAG_CATEGORY_ORDER = Object.freeze([
  "baseline_labs",
  "infection_labs",
  "glycemic_control",
  "nutrition_labs",
  "coagulation",
  "vascular_noninvasive",
  "perfusion",
  "vascular_invasive",
  "imaging_plain_film",
  "imaging_advanced",
  "biopsy",
  "other",
]);

function createPageSessionId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const random = Math.random().toString(16).slice(2).padEnd(12, "0").slice(0, 12);
  return `00000000-0000-4000-8000-${random}`;
}

function boundedArray(value, limit = 100) {
  return Array.isArray(value) ? value.slice(0, limit) : [];
}

function boundedStrings(value, limit = 20) {
  return boundedArray(value, limit)
    .filter((item) => typeof item === "string" && item.trim())
    .map((item) => item.trim());
}

function citationOrdinals(value) {
  return boundedArray(value).map((citation, index) => ({
    index:
      Number.isInteger(citation?.index) && citation.index > 0 && citation.index <= 10_000
        ? citation.index
        : index + 1,
  }));
}

function objectRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function selectQueryPayload(raw) {
  const envelope = objectRecord(raw);
  if (!envelope) return null;
  if (Object.hasOwn(envelope, "response")) return objectRecord(envelope.response);
  return DIRECT_CLINICAL_PAYLOAD_KEYS.some((key) => Object.hasOwn(envelope, key))
    ? envelope
    : null;
}

function normalizeConversationState(value) {
  const row = objectRecord(value);
  if (!row || typeof row.contract_version !== "string") return null;
  const activeWoundId = typeof row.active_wound_id === "string" ? row.active_wound_id : "wound-1";
  const questionWoundId = typeof row.question_wound_id === "string" ? row.question_wound_id : "wound-1";
  return {
    contract_version: row.contract_version,
    active_wound_id: activeWoundId,
    question_wound_id: questionWoundId,
    known_wound_ids: boundedStrings(row.known_wound_ids, 20),
    accepted_turn_ids: boundedStrings(row.accepted_turn_ids, 40),
    effective_turn_ids: boundedStrings(row.effective_turn_ids, 40),
    superseded_turn_ids: boundedStrings(row.superseded_turn_ids, 40),
    revision_count: Number.isInteger(row.revision_count) ? row.revision_count : 0,
    active_case_fingerprint:
      typeof row.active_case_fingerprint === "string" ? row.active_case_fingerprint : null,
    next_question_ids: boundedStrings(row.next_question_ids, 3),
  };
}

function normalizeDiagnostics(payload) {
  const values = boundedArray(payload.diagnostics);
  if (payload.consultation_contract_version !== "woundos-single-object-consultation-shadow-v1") {
    return values;
  }
  // Map the declared Action wire shape without inventing category, urgency,
  // coverage, or medical interpretation. Legacy diagnostic objects stay separate.
  if (payload.diagnostics != null && !Array.isArray(payload.diagnostics)) return null;
  if (Array.isArray(payload.diagnostics) && payload.diagnostics.length > 64) return null;
  const validText = (value) => typeof value === "string" && value.trim()
    && value.length <= 12000 && Array.from(value).length <= 6000;
  const output = [];
  for (const value of values) {
    const item = objectRecord(value);
    if (!item || item.kind !== "diagnostic" || !validText(item.text) || !validText(item.rationale)) {
      return null;
    }
    output.push({ name: item.text, indication: item.rationale, category: "other" });
  }
  return output;
}

function normalizeQueryResponse(raw) {
  const payload = selectQueryPayload(raw);
  if (!payload) return null;

  const disposition = normalizeRequestDisposition(payload.request_disposition);
  if (disposition?.mode === EVIDENCE_MODE || Object.hasOwn(payload, "evidence_contract_version")) {
    return normalizeEvidenceResponse(payload);
  }

  const clinicalInteraction = normalizeClinicalInteraction(payload.clinical_interaction);
  // An invalid declared clinical state is not an ungoverned legacy response.
  const declaresClinicalState = Object.hasOwn(payload, "consultation_contract_version")
    || payload.clinical_interaction != null;
  if (declaresClinicalState && !clinicalInteraction) return null;
  const blocked = interactionIsBlocked(clinicalInteraction);
  const reviewedConsultation = normalizeReviewedConsultation(payload, clinicalInteraction);
  if (reviewedConsultation === null) return null;
  const diagnostics = blocked ? [] : normalizeDiagnostics(payload);
  if (diagnostics === null) return null;
  return {
    response_type: "PATIENT",
    diagnosis: typeof payload.diagnosis === "string" ? payload.diagnosis : null,
    treatment_plan: blocked ? [] : boundedArray(payload.treatment_plan),
    plan_steps: blocked ? [] : boundedArray(payload.plan_steps),
    diagnostics,
    product_recommendations: blocked ? [] : boundedArray(payload.product_recommendations),
    product_option_contract_version:
      !blocked && typeof payload.product_option_contract_version === "string"
        ? payload.product_option_contract_version
        : null,
    product_selection_summary:
      !blocked && typeof payload.product_selection_summary === "string"
        ? payload.product_selection_summary
        : null,
    product_selection_disclaimer:
      !blocked && typeof payload.product_selection_disclaimer === "string"
        ? payload.product_selection_disclaimer
        : null,
    billing_codes: blocked ? [] : boundedArray(payload.billing_codes),
    lcd_ncd_summary:
      !blocked && typeof payload.lcd_ncd_summary === "string" ? payload.lcd_ncd_summary : null,
    answer: !blocked && typeof payload.answer === "string" ? payload.answer : null,
    answer_markdown:
      !blocked && typeof payload.answer_markdown === "string" ? payload.answer_markdown : null,
    clinical_rationale:
      typeof payload.clinical_rationale === "string" ? payload.clinical_rationale : null,
    explanation_for_clinician:
      typeof payload.explanation_for_clinician === "string"
        ? payload.explanation_for_clinician
        : null,
    referrals: blocked ? [] : boundedArray(payload.referrals),
    citations: blocked ? [] : citationOrdinals(payload.citations),
    reasoning: null,
    clinical_interaction: clinicalInteraction,
    reviewed_consultation: reviewedConsultation || null,
    conversation_state: normalizeConversationState(payload.conversation_state),
    request_disposition: disposition,
  };
}

function genericFailure(message) {
  return { ...EMPTY_RESPONSE, response_type: "UNAVAILABLE", reasoning: message };
}

function prettyToken(value, fallback = "Option") {
  if (typeof value !== "string" || !value.trim()) return fallback;
  return value
    .trim()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function Diagnostics({ diagnostics }) {
  if (!Array.isArray(diagnostics) || diagnostics.length === 0) return null;
  const grouped = Object.fromEntries(DIAG_CATEGORY_ORDER.map((category) => [category, []]));
  for (const diagnostic of diagnostics) {
    if (!diagnostic || typeof diagnostic !== "object") continue;
    const category = Object.hasOwn(grouped, diagnostic.category) ? diagnostic.category : "other";
    grouped[category].push(diagnostic);
  }
  const sections = DIAG_CATEGORY_ORDER.filter((category) => grouped[category].length > 0);
  if (sections.length === 0) return null;
  return (
    <section className="resp-section" aria-labelledby="diagnostics-heading">
      <h4 id="diagnostics-heading">🔬 Diagnostic workup (for the clinician)</h4>
      {sections.map((category) => (
        <div key={category} className="diag-block">
          <h5>{DIAG_CATEGORY_TITLES[category] || "Other diagnostics"}</h5>
          <ul>
            {grouped[category].map((diagnostic, index) => (
              <li key={`${category}-${index}`} className="diag-item">
                <strong>{diagnostic?.name || "Unnamed study"}</strong>
                {diagnostic?.indication && <div>{diagnostic.indication}</div>}
                {diagnostic?.urgency && <div>Urgency: {diagnostic.urgency}</div>}
                {diagnostic?.coverage_relevance && <div>Coverage: {diagnostic.coverage_relevance}</div>}
                {diagnostic?.notes && <div>{diagnostic.notes}</div>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function ProductRecommendations({ products, summary, disclaimer }) {
  if (!Array.isArray(products) || products.length === 0) return null;
  const groups = [];
  const byGroup = new Map();
  for (const product of products) {
    if (!product || typeof product !== "object") continue;
    const key = typeof product.selection_group === "string" && product.selection_group
      ? product.selection_group
      : "other";
    if (!byGroup.has(key)) {
      const group = {
        key,
        label:
          typeof product.selection_group_label === "string" && product.selection_group_label
            ? product.selection_group_label
            : prettyToken(key, "Other product options"),
        products: [],
      };
      byGroup.set(key, group);
      groups.push(group);
    }
    byGroup.get(key).products.push(product);
  }
  if (groups.length === 0) return null;

  return (
    <section className="resp-section" aria-labelledby="product-options-heading">
      <h4 id="product-options-heading">🧰 Product Selection and Alternatives</h4>
      {summary && <p>{summary}</p>}
      {groups.map((group) => (
        <div key={group.key} className="product-group">
          <h5>{group.label}</h5>
          <ol>
            {group.products.map((product, index) => {
              const useWhen = boundedStrings(product.use_when);
              const avoidWhen = boundedStrings(product.avoid_when);
              const where = boundedArray(product.where_in_plan, 12).filter(
                (item) => Number.isInteger(item) && item > 0,
              );
              return (
                <li key={`${group.key}-${product?.name || index}-${index}`} className="product-option">
                  <div>
                    <strong>{product?.name || "Unnamed product option"}</strong>
                    {product?.selection_rank && <span> — {prettyToken(product.selection_rank)}</span>}
                  </div>
                  {product?.generic_class && <div>Class: {product.generic_class}</div>}
                  {product?.category && product.category !== product.generic_class && (
                    <div>Category: {product.category}</div>
                  )}
                  {product?.rationale && <div>Why it fits: {product.rationale}</div>}
                  {product?.how_to_use && <div>How to use: {product.how_to_use}</div>}
                  {useWhen.length > 0 && (
                    <div>
                      <strong>Use when:</strong>
                      <ul>{useWhen.map((item) => <li key={item}>{item}</li>)}</ul>
                    </div>
                  )}
                  {avoidWhen.length > 0 && (
                    <div>
                      <strong>Avoid / reconsider when:</strong>
                      <ul>{avoidWhen.map((item) => <li key={item}>{item}</li>)}</ul>
                    </div>
                  )}
                  {product?.alternative_to && <div>Alternative to: {product.alternative_to}</div>}
                  {where.length > 0 && <div>Linked plan step(s): {where.join(", ")}</div>}
                  {product?.hcpcs && <div>HCPCS: {product.hcpcs}</div>}
                  {product?.sku && <div>SKU / size: {product.sku}</div>}
                  {product?.evidence_basis && <div>Basis: {prettyToken(product.evidence_basis)}</div>}
                </li>
              );
            })}
          </ol>
        </div>
      ))}
      {disclaimer && <p><small>{disclaimer}</small></p>}
    </section>
  );
}

function ClinicalInteractionPanel({ interaction, conversationState }) {
  if (!interaction) return null;
  const blocked = interactionIsBlocked(interaction);
  const conversation = interaction.conversation;
  return (
    <section className="resp-section clinical-interaction" aria-labelledby="clinical-interaction-heading">
      <h4 id="clinical-interaction-heading">🛡 Clinical interaction state</h4>
      <p><strong>{interaction.state.replaceAll("_", " ")}</strong> · {interaction.urgency.replaceAll("_", " ")}</p>
      {conversationState?.active_wound_id && (
        <p>Active wound: <strong>{conversationState.active_wound_id}</strong></p>
      )}
      {interaction.working_impression && <p>{interaction.working_impression}</p>}
      {interaction.emergency_triggers.length > 0 && (
        <div>
          <h5>Emergency findings</h5>
          <ul>{interaction.emergency_triggers.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      )}
      {interaction.contradictions.length > 0 && (
        <div>
          <h5>Contradictions requiring reconciliation</h5>
          <ul>{interaction.contradictions.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      )}
      {interaction.questions.length > 0 && (
        <div>
          <h5>Information needed before a definitive plan</h5>
          <ol>{interaction.questions.map((item) => <li key={item.question_id}>{item.text}</li>)}</ol>
        </div>
      )}
      {conversation?.remaining_domains?.length > 0 && (
        <p>Additional unresolved domains: {conversation.remaining_domains.map(prettyToken).join(", ")}</p>
      )}
      {interaction.immediate_actions.length > 0 && (
        <div>
          <h5>Safe actions while the case is being clarified</h5>
          <ul>{interaction.immediate_actions.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      )}
      {blocked && interaction.deferred_modalities.length > 0 && (
        <div>
          <h5>Deferred until prerequisites are established</h5>
          <p>{interaction.deferred_modalities.map(prettyToken).join(", ")}</p>
        </div>
      )}
      {conversation?.reply_prompt && <p><strong>How to reply:</strong> {conversation.reply_prompt}</p>}
    </section>
  );
}

function PromptButtons({ prompts, onUsePrompt, heading = "Related evidence prompts" }) {
  if (!Array.isArray(prompts) || prompts.length === 0) return null;
  return (
    <section className="resp-section">
      <h4>{heading}</h4>
      <ul>
        {prompts.map((item) => (
          <li key={item.prompt_id}>
            <button className="clear-btn" type="button" onClick={() => onUsePrompt(item.prompt)}>
              {item.title}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function EvidenceResponse({ result, onUsePrompt }) {
  const narrative = result.answer || result.answer_markdown;
  return (
    <div className="response" aria-live="polite" aria-atomic="false">
      <section className="resp-section" aria-labelledby="evidence-response-heading">
        <h4 id="evidence-response-heading">📚 Referenced evidence synthesis</h4>
        <p>{narrative}</p>
      </section>
      <section className="resp-section">
        <h4>Key evidence points</h4>
        <ul>
          {result.evidence_key_points.map((point, index) => (
            <li key={`${index}-${point.text}`}>
              {point.text} {point.citation_indices.map((citationIndex) => `[${citationIndex}]`).join(" ")}
            </li>
          ))}
        </ul>
      </section>
      {result.limitations.length > 0 && (
        <section className="resp-section">
          <h4>Limits / context</h4>
          <ul>{result.limitations.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
      )}
      <section className="resp-section">
        <h4>🔍 References</h4>
        <ol>
          {result.citations.map((citation) => (
            <li key={citation.index}>
              <strong>[{citation.index}] {citation.source}</strong>
              {citation.topic ? ` — ${citation.topic}` : ""}
              {citation.doc_type ? ` (${citation.doc_type})` : ""}
              {citation.loc ? ` · ${citation.loc}` : ""}
            </li>
          ))}
        </ol>
      </section>
      <PromptButtons prompts={result.related_prompts} onUsePrompt={onUsePrompt} />
    </div>
  );
}

function ClinicalResponse({ result, onUsePrompt }) {
  if (!result) return null;
  if (result.response_type === "UNAVAILABLE") {
    return <div role="alert" className="response">
      <p>{result.reasoning}</p>
      <p>No completed clinical answer was delivered.</p>
    </div>;
  }
  if (result.response_type === "EMERGENCY_NOTICE") {
    return <div role="alert" className="response">
      <p><strong>Obtain immediate in-person emergency assessment.</strong> Activate the local emergency response or contact the responsible surgical team now. Do not wait for another chatbot response.</p>
      <p>The full consultation is unavailable. This safety notice is not a completed diagnosis or treatment plan.</p>
    </div>;
  }
  if (result.response_type === "EVIDENCE") {
    return <EvidenceResponse result={result} onUsePrompt={onUsePrompt} />;
  }
  const rationale = result.clinical_rationale || result.explanation_for_clinician || result.reasoning;
  if (result.reviewed_consultation) {
    return <ReviewedConsultation value={result.reviewed_consultation} diagnosis={result.diagnosis}
      interaction={result.clinical_interaction} rationale={rationale} />;
  }
  const narrative = result.answer_markdown || result.answer;
  return (
    <div className="response" aria-live="polite" aria-atomic="false">
      <ClinicalInteractionPanel
        interaction={result.clinical_interaction}
        conversationState={result.conversation_state}
      />
      {result.diagnosis && (
        <section className="resp-section"><h4>📋 Diagnosis</h4><p>{result.diagnosis}</p></section>
      )}
      {result.treatment_plan.length > 0 && (
        <section className="resp-section">
          <h4>🧴 Treatment Plan</h4>
          <ol>{result.treatment_plan.map((step, index) => <li key={index}>{String(step)}</li>)}</ol>
        </section>
      )}
      {result.plan_steps.length > 0 && (
        <section className="resp-section">
          <h4>🧭 Plan Steps</h4>
          <ol>{result.plan_steps.map((step, index) => (
            <li key={index}>
              <strong>{step?.title || `Step ${index + 1}`}</strong>
              {step?.description ? ` — ${step.description}` : ""}
            </li>
          ))}</ol>
        </section>
      )}
      <Diagnostics diagnostics={result.diagnostics} />
      <ProductRecommendations
        products={result.product_recommendations}
        summary={result.product_selection_summary}
        disclaimer={result.product_selection_disclaimer}
      />
      {result.billing_codes.length > 0 && (
        <section className="resp-section">
          <h4>💳 Billing Codes</h4>
          <ul>{result.billing_codes.map((code, index) => <li key={index}>{code?.code || "Code unavailable"}</li>)}</ul>
        </section>
      )}
      {result.lcd_ncd_summary && (
        <section className="resp-section"><h4>📜 LCD/NCD Summary</h4><p>{result.lcd_ncd_summary}</p></section>
      )}
      {result.referrals.length > 0 && (
        <section className="resp-section">
          <h4>📤 Referrals</h4>
          <ul>{result.referrals.map((item, index) => <li key={index}>{String(item)}</li>)}</ul>
        </section>
      )}
      {rationale && (
        <section className="resp-section"><h4>🧠 Clinical Rationale</h4><p>{rationale}</p></section>
      )}
      {narrative && (
        <section className="resp-section">
          <details open><summary>🗒 Full narrative / markdown</summary><pre className="answer-pre">{narrative}</pre></details>
        </section>
      )}
      {result.citations.length > 0 && (
        <section className="resp-section">
          <h4>🔍 Citations</h4>
          <p className="citations-row">{result.citations.map((citation) => `[${citation.index}]`).join(" ")}</p>
        </section>
      )}
    </div>
  );
}

function PreviousReply({ entry, onUsePrompt }) {
  const [open, setOpen] = useState(false);
  return <section className="previous-reply">
    <button type="button" className="clear-btn" aria-expanded={open}
      onClick={() => setOpen((value) => !value)}>{open ? "Hide reply" : "Show reply"}</button>
    {open && <><p><strong>You:</strong> {entry.question}</p>
      <ClinicalResponse result={entry.response} onUsePrompt={onUsePrompt} /></>}
  </section>;
}

export default function App({ request = fetch, apiBase } = {}) {
  const questionId = useId();
  const [question, setQuestion] = useState("");
  const [intakeResetKey, setIntakeResetKey] = useState(0);
  const [intakePending, setIntakePending] = useState(false);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [caseAnchor, setCaseAnchor] = useState(null);
  const [clinicalTurns, setClinicalTurns] = useState([]);
  const [acceptedQuestions, setAcceptedQuestions] = useState([]);
  const [activeWoundId, setActiveWoundId] = useState("wound-1");
  const [questionWoundId, setQuestionWoundId] = useState("wound-1");
  const [promptLibrary, setPromptLibrary] = useState(null);
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState(null);
  const [sessionId] = useState(createPageSessionId);

  const base = apiBase ?? (
    import.meta.env?.VITE_BACKEND_URL
      ? String(import.meta.env.VITE_BACKEND_URL)
      : window.location.origin
  ).replace(/\/+$/, "");
  const previousReplies = response === chatHistory.at(-1)?.response
    ? chatHistory.slice(0, -1) : chatHistory;
  const queryUrl = `${base}/query`;
  const promptsUrl = `${base}/prompts`;

  const usePrompt = useCallback((prompt) => {
    setQuestion(String(prompt || "").slice(0, 4000));
    setPromptLibraryOpen(false);
  }, []);

  const resetEpisode = useCallback(() => {
    setQuestion("");
    setIntakeResetKey((value) => value + 1);
    setIntakePending(false);
    setCaseAnchor(null);
    setClinicalTurns([]);
    setAcceptedQuestions([]);
    setActiveWoundId("wound-1");
    setQuestionWoundId("wound-1");
  }, []);

  const handlePromptLibrary = useCallback(async () => {
    if (promptLibraryOpen) {
      setPromptLibraryOpen(false);
      return;
    }
    if (promptLibrary) {
      setPromptLibraryOpen(true);
      return;
    }
    if (promptLoading) return;
    setPromptLoading(true);
    setPromptError(null);
    try {
      const result = await request(promptsUrl, { method: "GET", headers: { Accept: "application/json" } });
      if (!result.ok) throw new Error("prompt library unavailable");
      const decoded = JSON.parse(await result.text());
      const normalized = normalizePromptLibrary(decoded);
      if (!normalized) throw new Error("invalid prompt library");
      setPromptLibrary(normalized);
      setPromptLibraryOpen(true);
    } catch {
      setPromptError("Evidence prompts are temporarily unavailable.");
    } finally {
      setPromptLoading(false);
    }
  }, [promptLibrary, promptLibraryOpen, promptLoading, promptsUrl, request]);

  const handleAsk = useCallback(async () => {
    const normalizedQuestion = question.trim();
    if (!normalizedQuestion || loading || intakePending) return;
    let governedRequest;
    try {
      governedRequest = buildGovernedRequest({
        caseAnchor,
        input: normalizedQuestion,
        sessionId,
        turns: clinicalTurns,
        pendingQuestions: acceptedQuestions,
        activeWoundId,
        questionWoundId,
      });
    } catch (error) {
      let message = "AskWoundBot could not process this request safely.";
      if (error?.message === "conversation_turn_limit") {
        message = "This conversation has reached its turn limit. Start a new case with the current findings. Your reply has been kept.";
      } else if (error?.message === "conversation_size_limit") {
        message = "This conversation is too long to send safely. Shorten this reply or start a new case with the current findings. Your reply has been kept.";
      } else if (error?.message === "conversation_question_limit") {
        message = "The previous questions could not be linked safely. Start a new case with the current findings. Your reply has been kept.";
      }
      setResponse(genericFailure(message));
      return;
    }

    setLoading(true);
    setResponse(null);
    try {
      const result = await request(queryUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(governedRequest.body),
      });
      if (!result.ok) {
        if (result.status === 413) {
          setResponse(genericFailure("This conversation is too long to send safely. Shorten this reply or start a new case with the current findings. Your reply has been kept."));
          return;
        }
        if (result.status === 422) {
          try {
            const failure = JSON.parse(await result.text());
            if (failure?.code === "CORRECTION_TARGET_UNCLEAR") {
              setResponse(genericFailure("Which earlier finding did you mean to correct? Please name that finding and its replacement. If several entries need changing, start a new case with the current details. Your reply has been kept."));
              return;
            }
          } catch {
            // Arbitrary server error text is never rendered as clinical advice.
          }
        }
        if (result.status === 429) {
          setResponse(genericFailure("The request limit has been reached. Please try again later."));
          return;
        }
        if (result.status === 503) {
          try {
            const failure = JSON.parse(await result.text());
            if (isEmergencyFailure(result.status, failure)) {
              setResponse({ ...EMPTY_RESPONSE, response_type: "EMERGENCY_NOTICE" });
              return;
            }
            if (failure?.detail === "The clinical service is at capacity.") {
              setResponse(genericFailure("AskWoundBot is busy. Please wait for the current consultation to finish, then try again."));
              return;
            }
          } catch {
            // Malformed errors remain generic; failed provider text is never shown.
          }
        }
        setResponse(genericFailure("AskWoundBot could not process this request safely."));
        return;
      }

      let decoded;
      try {
        decoded = JSON.parse(await result.text());
      } catch {
        setResponse(genericFailure("AskWoundBot returned an invalid response."));
        return;
      }
      const normalizedResponse = normalizeQueryResponse(decoded);
      if (!normalizedResponse) {
        setResponse(isEmergencyDeliveryFallback(result.status, selectQueryPayload(decoded))
          ? { ...EMPTY_RESPONSE, response_type: "EMERGENCY_NOTICE" }
          : genericFailure("AskWoundBot returned an invalid response."));
        return;
      }

      setResponse(normalizedResponse);
      const interaction = normalizedResponse.clinical_interaction;
      if (normalizedResponse.response_type === "PATIENT" && interaction) {
        // Preserve the complete patient episode after clarification, READY, and urgent
        // responses. The clinician can therefore correct a fact, add interval findings,
        // report a new danger signal that re-opens the plan, or introduce a separately
        // isolated wound without silently restarting the case.
        // Transport/schema errors must not erase the last accepted question link.
        setAcceptedQuestions(interaction.questions);
        setCaseAnchor(governedRequest.original);
        setClinicalTurns(governedRequest.clinicalTurns);
        setActiveWoundId(
          normalizedResponse.conversation_state?.active_wound_id ||
          governedRequest.activeWoundId ||
          activeWoundId,
        );
        setQuestionWoundId(
          normalizedResponse.conversation_state?.question_wound_id ||
          governedRequest.questionWoundId ||
          questionWoundId,
        );
      } else {
        resetEpisode();
      }
      setChatHistory((current) => [
        ...current.slice(-19),
        { question: normalizedQuestion, response: normalizedResponse, timestamp: new Date().toISOString() },
      ]);
      setQuestion("");
    } catch {
      setResponse(genericFailure("AskWoundBot could not connect to the service safely."));
    } finally {
      setLoading(false);
    }
  }, [
    acceptedQuestions,
    activeWoundId,
    caseAnchor,
    clinicalTurns,
    intakePending,
    loading,
    queryUrl,
    request,
    question,
    questionWoundId,
    resetEpisode,
    sessionId,
  ]);

  return (
    <div className="layout">
      <aside className="sidebar" aria-labelledby="history-heading">
        <h2 id="history-heading">In-memory history</h2>
        <p className="privacy-note">This history exists only in the open page and is cleared on reload or close.</p>
        {chatHistory.length === 0 ? <p><em>No history yet.</em></p> : (
          <ol className="history-list">
            {chatHistory.map((entry, index) => <li key={`${entry.timestamp}-${index}`}>{entry.question}</li>)}
          </ol>
        )}

      </aside>

      <main className="main" id="main-content">
        <header>
          <h1>AskWoundBot</h1>
          <p>Clinician-facing decision support and referenced evidence search. Review all output against the patient record, primary evidence and your independent clinical judgment.</p>
        </header>

        <ManufacturerProductDirectory />
        <ProductEvidenceLibrary />

        <CaseIntake key={intakeResetKey} question={question} onInsert={setQuestion} onPendingChange={setIntakePending} disabled={loading} episodeActive={Boolean(caseAnchor)} activeWoundId={activeWoundId} />

        <div className="input-group">
          <label htmlFor={questionId}>Clinical question</label>
          <textarea
            id={questionId}
            className="ask-textarea"
            disabled={loading}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleAsk();
              }
            }}
            placeholder="Type your wound care question here..."
            maxLength={4000}
            aria-describedby="question-help"
          />
          <p id="question-help">
            {caseAnchor
              ? "Continue this wound episode in ordinary clinical shorthand. Prior facts remain locked. Start with ‘Correction:’ to replace an erroneous prior entry or ‘New wound:’ to open a separate wound state."
              : "Ask a patient-specific wound question or a general evidence question. Do not enter patient-identifying information unless your organization has separately authorized a PHI-capable deployment."}
          </p>
          {intakePending && <p role="status">Insert the structured details into the question, or reset those fields, before asking.</p>}
          <button className="ask-button" onClick={handleAsk} disabled={loading || intakePending || !question.trim()} aria-label="Ask" type="button">
            {loading ? "Asking…" : "Ask"}
          </button>
          <button className="clear-btn" disabled={loading} type="button"
            onClick={() => { setResponse(null); resetEpisode(); }}>
            Start new case
          </button>
          <button className="clear-btn" onClick={handlePromptLibrary} disabled={promptLoading} type="button">
            {promptLoading ? "Loading evidence prompts…" : promptLibraryOpen ? "Hide evidence prompts" : "Browse evidence prompts"}
          </button>
        <button
          className="clear-btn"
          disabled={loading}
          onClick={() => {
            setChatHistory([]);
            setResponse(null);
            resetEpisode();
            setPromptLibraryOpen(false);
          }}
          type="button"
        >
          Clear history
        </button>
          {promptError && <p role="alert">{promptError}</p>}
        </div>

        {promptLibraryOpen && promptLibrary && (
          <section aria-labelledby="prompt-library-heading">
            <h2 id="prompt-library-heading" className="section-title">Curated WoundOS Evidence Prompts</h2>
            <PromptButtons prompts={promptLibrary.prompts} onUsePrompt={usePrompt} heading="Point-of-care starting questions" />
          </section>
        )}

        <div role="status" aria-live="polite" className="sr-status">
          {loading ? "Preparing your consultation." : ""}
        </div>

        {previousReplies.length > 0 && <section aria-label="Previous replies">
          {previousReplies.map((entry, index) =>
            <PreviousReply key={entry.timestamp + index} entry={entry} onUsePrompt={usePrompt} />)}
        </section>}

        {response && (
          <section aria-labelledby="response-heading">
            <h2 id="response-heading" className="section-title">
              {response.response_type === "UNAVAILABLE" ? "Consultation unavailable"
                : response.response_type === "EMERGENCY_NOTICE" ? "Emergency safety notice"
                : response.response_type === "EVIDENCE" ? "Referenced Evidence Response" : "Structured Clinical Response"}
            </h2>
            <ClinicalResponse result={response} onUsePrompt={usePrompt} />
            {caseAnchor && response.clinical_interaction?.state !== "URGENT_ESCALATION" &&
              response.response_type === "PATIENT" &&
              <button type="button" className="ask-button" disabled={loading}
                onClick={() => document.getElementById(questionId)?.focus()}>
                Reply to AskWoundBot
              </button>}
          </section>
        )}

        <footer className="footer">
          <p>Clinically Engineered by <strong>Hoodnurse®</strong></p>
          <p>© {new Date().getFullYear()} AskWoundBot. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
}
